import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { CircularProgress, IconButton, Radio, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { BOARD_TYPES_ALIAS } from '~/consts/ratePlans';

import type { OfferRatePlan } from '~/hooks/apis/reservation/useOfferRatePlans';

import dateFormatter from '~/utils/dateFormatter';

const sharedOpts: Partial<GridColDef<OfferRatePlan>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const unsortableOpts: Partial<GridColDef<OfferRatePlan>> = {
  ...sharedOpts,
  sortable: false,
};

function getColumns({
  onClone,
  onSetDefault,
  onEdit,
  loadingEditRatePlanId,
}: {
  onClone: (ratePlan: OfferRatePlan) => void;
  onSetDefault: (ratePlan: OfferRatePlan) => void;
  onEdit: (ratePlan: OfferRatePlan) => void;
  loadingEditRatePlanId?: string | null;
}): Array<GridColDef<OfferRatePlan>> {
  return [
    {
      field: 'name',
      headerName: 'Name',
      flex: 2,
      renderCell: ({ row }) => (row.rate_plan_code ? `${row.name} (${row.rate_plan_code})` : row.name),
      ...sharedOpts,
    },
    {
      field: 'group',
      headerName: 'Group',
      flex: 1,
      valueGetter: (_value, row) => row.group?.name ?? '-',
      ...sharedOpts,
    },
    {
      field: 'default_plan',
      headerName: 'Default',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Radio
          checked={!!row.default_plan}
          color="primary"
          name="default_plan"
          size="small"
          onChange={() => onSetDefault(row)}
        />
      ),
      ...unsortableOpts,
    },
    {
      field: 'scheduled',
      headerName: 'Scheduled',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) =>
        row.schedules_list?.length ? <CheckCircleIcon color="primary" fontSize="small" /> : '-',
      ...sharedOpts,
    },
    {
      field: 'is_packaged_rate',
      headerName: 'Packaged',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) =>
        row.is_packaged_rate ? (
          <Stack alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
            <CheckCircleIcon color="primary" fontSize="small" />
          </Stack>
        ) : (
          '-'
        ),
      ...sharedOpts,
    },
    {
      field: 'board_type',
      headerName: 'Board Group',
      flex: 1,
      valueGetter: (_value, row) => (row.board_type ? (BOARD_TYPES_ALIAS[row.board_type] ?? row.board_type) : '-'),
      ...sharedOpts,
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      flex: 1,
      type: 'date',
      valueGetter: (_value, row) => (row.created_at ? new Date(row.created_at) : null),
      renderCell: ({ row }) => (row.created_at ? dateFormatter(row.created_at) : '-'),
      ...sharedOpts,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) => {
        const isLoadingEdit = loadingEditRatePlanId === row.id;
        return (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ width: '100%' }}>
            <IconButton aria-label={`Clone ${row.name}`} size="small" onClick={() => onClone(row)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label={`Edit ${row.name}`}
              size="small"
              onClick={() => onEdit(row)}
              disabled={isLoadingEdit}
            >
              {isLoadingEdit ? <CircularProgress size={16} /> : <EditIcon fontSize="small" />}
            </IconButton>
          </Stack>
        );
      },
      ...unsortableOpts,
    },
  ];
}

interface Props {
  ratePlans: Array<OfferRatePlan>;
  loading?: boolean;
  onClone: (ratePlan: OfferRatePlan) => void;
  onSetDefault: (ratePlan: OfferRatePlan) => void;
  onEdit: (ratePlan: OfferRatePlan) => void;
  loadingEditRatePlanId?: string | null;
}

export default function RatePlansTable({
  ratePlans,
  loading,
  onClone,
  onSetDefault,
  onEdit,
  loadingEditRatePlanId,
}: Props) {
  return (
    <DataGrid
      autoHeight
      disableRowSelectionOnClick
      columns={getColumns({ onClone, onSetDefault, onEdit, loadingEditRatePlanId })}
      loading={loading}
      rows={ratePlans}
      getRowHeight={() => 'auto'}
      initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
      pageSizeOptions={[25, 50, 100]}
      sx={{ '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex' } }}
    />
  );
}
